import { build_data } from '@shapediver/viewer.shared.build-data';
import {
    BUSY_MODE_DISPLAY,
    SESSION_SETTINGS_MODE,
    SPINNER_POSITIONING,
    VISIBILITY_MODE
    } from '@shapediver/viewer.rendering-engine.rendering-engine';
import {
    CreationControlCenter,
    ICreationControlCenter,
    SessionCreationDefinition,
    ViewportCreationDefinition
    } from '@shapediver/viewer.main.creation-control-center';
import {
    EventEngine,
    HttpClient,
    IEvent,
    LOGGING_LEVEL,
    MainEventTypes,
    ShapeDiverViewerSessionError,
    ShapeDiverViewerValidationError
    } from '@shapediver/viewer.shared.services';
import { GeometryEngine } from '@shapediver/viewer.data-engine.geometry-engine';
import { InputValidator } from '@shapediver/viewer.shared.services';
import { ISessionApi } from './interfaces/session/ISessionApi';
import { ITree, Tree } from '@shapediver/viewer.shared.node-tree';
import { IViewportApi } from './interfaces/viewport/IViewportApi';
import { Logger } from '@shapediver/viewer.shared.services';
import { RenderingEngine as RenderingEngineThreeJs } from '@shapediver/viewer.rendering-engine-threejs.standard';
import { SessionApi } from './implementation/session/SessionApi';
import { SessionEngine } from '@shapediver/viewer.session-engine.session-engine';
import { ViewportApi } from './implementation/viewport/ViewportApi';

const creationControlCenter: ICreationControlCenter = CreationControlCenter.instance;
const httpClient: HttpClient = HttpClient.instance;
const inputValidator: InputValidator = InputValidator.instance;
const logger: Logger = Logger.instance;
const eventEngine: EventEngine = EventEngine.instance;
const geometryEngine: GeometryEngine = GeometryEngine.instance;

let createdConsoleMessage = false, consoleBranding = true;

export interface IGeneralOptions {
    // #region Properties (5)

    /**
     * Caching options.
     */
    caching: {
        /**
         * Option to enable/disable the caching. (default: true)
         */
        enable: boolean;

        /**
         * Query parameters that are excluded when creating the key that is used for the comparison of data entries in the cache. (default: ['Expires', 'Signature', 'Key-Pair-Id'])
         * Example: by the addition of 'Expires' the URLs https://link.example?Expires=0 and https://link.example?Expires=1 are both reduced to https://link.example for caching.
         */
        excludedQueryParameters: string[];

        /**
         * The maximum size of the cache in bytes. (default: 1024^3)
         */
        maxCacheSize: number;
    }

    /**
     * When set to false, the branding in the viewer console will be limited to a single line.
     * This will only include the viewer version. (default: true)
     */
    consoleBranding: boolean;
    /**
     * The logging level that is used.
     */
    loggingLevel: LOGGING_LEVEL;
    /**
     * The number of glTFs that are downloaded and processed at the same time.
     * Restricting this number might help if too much data is downloaded at the same time.
     * (default: Infinity)
     */
    parallelGlTFProcessing: number;
    /**
     * Option to show/hide messages in the browser console.
     */
    showMessages: boolean;

    // #endregion Properties (5)
}

class GeneralOptions {
    // #region Public Accessors (10)

    public get caching(): { enable: boolean, excludedQueryParameters: string[], maxCacheSize: number } {
        return {
            enable: httpClient.enableCaching,
            excludedQueryParameters: httpClient.excludedQueryParameters,
            maxCacheSize: httpClient.maxCacheSize
        };
    }

    public set caching(value: { enable: boolean, excludedQueryParameters: string[], maxCacheSize: number }) {
        inputValidator.validateAndError('caching', value, 'object', true);
        inputValidator.validateAndError('caching', value.enable, 'boolean');
        inputValidator.validateAndError('caching', value.excludedQueryParameters, 'stringArray');
        inputValidator.validateAndError('caching', value.maxCacheSize, 'number');
        if (value.enable !== undefined) httpClient.enableCaching = value.enable;
        if (value.excludedQueryParameters !== undefined) httpClient.excludedQueryParameters = value.excludedQueryParameters;
        if (value.maxCacheSize !== undefined) httpClient.maxCacheSize = value.maxCacheSize;
        logger.debug(`caching: CoggingLevel was set to: ${value}`);
    }

    public get consoleBranding(): boolean {
        return consoleBranding;
    }

    public set consoleBranding(value: boolean) {
        inputValidator.validateAndError('consoleBranding', value, 'boolean');
        consoleBranding = value;
        logger.debug(`consoleBranding: ConsoleBranding was set to: ${value}`);
    }

    public get loggingLevel(): LOGGING_LEVEL {
        return logger.loggingLevel;
    }

    public set loggingLevel(value: LOGGING_LEVEL) {
        inputValidator.validateAndError('loggingLevel', value, 'enum', true, Object.values(LOGGING_LEVEL));
        logger.loggingLevel = value;
        logger.debug(`loggingLevel: LoggingLevel was set to: ${value}`);
    }

    public get parallelGlTFProcessing(): number {
        return geometryEngine.parallelGlTFProcessing;
    }

    public set parallelGlTFProcessing(value: number) {
        inputValidator.validateAndError('parallelGlTFProcessing', value, 'number');
        geometryEngine.parallelGlTFProcessing = value;
        logger.debug(`parallelGlTFProcessing: ParallelGlTFProcessing was set to: ${value}`);
    }

    public get showMessages(): boolean {
        return logger.showMessages;
    }

    public set showMessages(value: boolean) {
        inputValidator.validateAndError('showMessages', value, 'boolean');
        logger.showMessages = value;
        logger.debug(`showMessages: ShowMessages was set to: ${value}`);
    }

    // #endregion Public Accessors (10)
}

/**
 * Adds an event listener.
 * 
 * @param type The type of event.
 * @param cb The callback.
 * @returns 
 */
export const addListener = (type: string | MainEventTypes, cb: (event: IEvent) => void): string => {
    inputValidator.validateAndError('addListener', type, 'string');
    inputValidator.validateAndError('addListener', cb, 'function');
    logger.debug(`addListener: Event Listener was registered for ${type}.`);
    return eventEngine.addListener(type, cb);
};

/**
 * Removes an event listener.
 * 
 * @param id The id of the listener.
 * @returns 
 */
export const removeListener = (id: string): boolean => {
    inputValidator.validateAndError('removeListener', id, 'string');
    logger.debug(`removeListener: Removing event listener with id ${id}.`);
    return eventEngine.removeListener(id);
};

/**
 * The scene tree that is used to store the scene.
 * The scene tree contains a unique node and child nodes for each session, 
 * and can also be used to add your own nodes.
 */
export const sceneTree: ITree = Tree.instance;

/**
 * The viewports that are currently being used.
 */
export const viewports: { [key: string]: IViewportApi; } = {};

/**
 * The sessions that are currently being used.
 */
export const sessions: { [key: string]: ISessionApi; } = {};

/**
 * The version of the viewer.
 */
export const version: string = build_data.build_version.replace('3.', '');

// Whenever a session or viewport is added or removed, this update is called.
creationControlCenter.update = (
    sessionEngines: { [key: string]: SessionEngine; },
    renderingEngines: { [key: string]: RenderingEngineThreeJs; }
) => {
    for (const s in sessionEngines)
        if (!sessions[s])
            sessions[s] = new SessionApi(sessionEngines[s]);

    for (const s in sessions)
        if (!sessionEngines[s])
            delete sessions[s];

    for (const v in renderingEngines)
        if (!viewports[v])
            viewports[v] = new ViewportApi(renderingEngines[v]);

    for (const v in viewports) {
        if (!renderingEngines[v])
            delete viewports[v];
    }
};

const showConsoleMessage = () => {
    createdConsoleMessage = true;
    if (consoleBranding === true) {
        console.log(`Powered by:
   _____  __                         ____   _                   
  / ___/ / /_   ____ _ ____   ___   / __ \\ (_)_   __ ___   _____
  \\__ \\ / __ \\ / __ '// __ \\ / _ \\ / / / // /| | / // _ \\ / ___/
 ___/ // / / // /_/ // /_/ //  __// /_/ // / | |/ //  __// /    
/____//_/ /_/ \\__,_// .___/ \\___//_____//_/  |___/ \\___//_/     
                   /_/                                          
ShapeDiver Viewer 3, Version ${build_data.build_version.replace('3.', '')}
Visit us at https://shapediver.com/ and find out more!
`);
    } else {
        console.log(`ShapeDiver Viewer 3, Version ${build_data.build_version.replace('3.', '')}`);
    }
};

/**
 * General Viewer options that are used everywhere.
 * - loggingLevel: The logging level that is used.
 * - showMessages: Option to show/hide messages in the browser console.
 * - parallelGlTFProcessing: The number of glTFs that are downloaded and processed at the same time.
 * - consoleBranding: When set to false, the branding in the viewer console will be limited to a single line.
 * - caching: Caching Options.
 */
export const generalOptions: IGeneralOptions = new GeneralOptions();

/**
 * Create and initialize a session with a model hosted on a 
 * {@link https://help.shapediver.com/doc/Geometry-Backend.1863942173.html|ShapeDiver Geometry Backend}, 
 * using the provided ticket/guid and modelViewUrl. 
 * Returns a session api object allowing to control the session.
 * 
 * A JWT can be specified for authorizing the API calls to the Geometry Backend. 
 * The model's settings on the Geometry Backend might require a JWT to be provided.
 *
 * By default the outputs of the model for its default parameter values will be loaded.
 * 
 * An optional identifier for the session can be provided. This identifier can be used to retrieve the  
 * api object from {@link sessions}. In case no identifier is provided, a unique one will be generated.
 * 
 * @param properties.ticket The ticket for direct embedding of the model represented by the session. This identifies the model on the Geometry Backend. If no ticket was provided, a {@link guid} has to be provided instead.
 * @param properties.guid The geometry backend model id (guid). This identifies the model on the Geometry Backend. A {@link jwtToken} is needed for authentication. If no guid was provided, a {@link ticket} has to be provided instead.
 * @param properties.modelViewUrl The modelViewUrl of the {@link https://help.shapediver.com/doc/Geometry-Backend.1863942173.html|ShapeDiver Geometry Backend} hosting the model.
 * @param properties.jwtToken The JWT to use for authorizing the API calls to the Geometry Backend.
 * @param properties.id The unique identifier to use for the session.
 * @param properties.waitForOutputs Option to wait for the outputs to be loaded, or return immediately after creation of the session. (default: true)
 * @param properties.loadOutputs Option to load the outputs, or not load them until the first call of {@link ISession.customize}. (default: true)
 * @param properties.excludeViewports Option to exclude some viewports from the start. Can be accessed via {@link ISession.excludeViewports}.
 * @param properties.initialParameterValues The initial set of parameter values to use. Map from parameter id to parameter value. The default value will be used for any parameter not specified.
 * @returns 
 */
export const createSession = async (properties: SessionCreationDefinition): Promise<ISessionApi> => {
    if (createdConsoleMessage === false) showConsoleMessage();

    logger.debug(`createSession: Creating and initializing session with properties ${JSON.stringify(properties)}.`);
    // input validation
    inputValidator.validateAndError('createSession', properties, 'object');
    inputValidator.validateAndError('createSession', properties.ticket, 'string', false);
    inputValidator.validateAndError('createSession', properties.guid, 'string', false);
    inputValidator.validateAndError('createSession', properties.modelViewUrl, 'string');
    inputValidator.validateAndError('createSession', properties.jwtToken, 'string', false);
    inputValidator.validateAndError('createSession', properties.id, 'string', false);
    inputValidator.validateAndError('createSession', properties.waitForOutputs, 'boolean', false);
    inputValidator.validateAndError('createSession', properties.loadOutputs, 'boolean', false);
    inputValidator.validateAndError('createSession', properties.excludeViewports, 'stringArray', false);
    inputValidator.validateAndError('createSession', properties.initialParameterValues, 'object', false);
    if (properties.initialParameterValues)
        for (const p in properties.initialParameterValues)
            inputValidator.validateAndError('createSession', properties.initialParameterValues[p], 'string');

    // we either expect a ticket or guid + jwtToken, error if we get both
    if (properties.ticket !== undefined && properties.guid !== undefined)
        throw new ShapeDiverViewerSessionError('createSession: A ticket and a guid were provided for the session creation. Please only provide one or the other. The session was not created.');

    // we either expect a ticket or guid + jwtToken, error if we get none
    if (properties.ticket === undefined && properties.guid === undefined)
        throw new ShapeDiverViewerSessionError('createSession: Neither a ticket nor a guid were provided for the session creation. Please provide one or the other. The session was not created.');

    // we either expect a guid + jwtToken, error if the jwtToken is missing
    if (properties.guid !== undefined && properties.jwtToken === undefined)
        throw new ShapeDiverViewerSessionError('createSession: When creating a session with a guid, a jwtToken is required, please provide one. The session was not created.');

    if (properties.waitForOutputs === undefined) properties.waitForOutputs = true;
    if (properties.loadOutputs === undefined) properties.loadOutputs = true;

    const sessionEngine = await creationControlCenter.createSessionEngine(properties);
    sessions[sessionEngine.id] = new SessionApi(sessionEngine);
    return sessions[sessionEngine.id];
};

/**
 * Create and initialize a viewport with the provided type and canvas, 
 * and return a viewport api object allowing to control it.
 * 
 * An optional identifier for the viewport can be provided. This identifier can be used to retrieve the  
 * viewport object from {@link viewports}. In case no identifier is provided, a unique one will be generated.
 * 
 * By default a new viewport displays the complete scene tree. Viewports can be excluded from 
 * displaying geometry for specific sessions by using the {@link excludeViewports} property of
 * {@link ISessionApi}.
 * 
 * @param properties.visibility The visibility of the viewport.
 * @param properties.canvas The canvas that the viewport should use. A canvas element will be created if none is provided. 
 * @param properties.id The unique identifier to use for the viewport.
 * @param properties.branding Optional branding options.
 * @param properties.sessionSettingsId Optional identifier of the session to be used for loading / persisting settings of the viewport when the {@link SESSION_SETTINGS_MODE} is set to MANUAL. 
 * @param properties.sessionSettingsMode Allows to control which session to use for loading / persisting settings of the viewport. (default: {@link SESSION_SETTINGS_MODE.FIRST}).
 * @returns 
 */
export const createViewport = async (properties?: ViewportCreationDefinition): Promise<IViewportApi> => {
    if (createdConsoleMessage === false) showConsoleMessage();

    inputValidator.validateAndError('createViewport', properties, 'object', false);

    const prop = Object.assign({}, properties);
    inputValidator.validateAndError('createViewport', prop.canvas, 'HTMLCanvasElement', false);
    inputValidator.validateAndError('createViewport', prop.id, 'string', false);
    inputValidator.validateAndError('createViewport', prop.sessionSettingsId, 'string', false);
    inputValidator.validateAndError('createViewport', prop.sessionSettingsMode, 'enum', false, Object.values(SESSION_SETTINGS_MODE));
    inputValidator.validateAndError('createViewport', prop.visibility, 'enum', false, Object.values(VISIBILITY_MODE));

    inputValidator.validateAndError('createViewport', prop.branding, 'object', false);
    const branding = Object.assign({}, prop.branding);
    if (branding.logo !== null) inputValidator.validateAndError('createViewport', branding.logo, 'string', false);
    inputValidator.validateAndError('createViewport', branding.backgroundColor, 'string', false);
    inputValidator.validateAndError('createViewport', branding.busyModeSpinner, 'string', false);
    inputValidator.validateAndError('createViewport', branding.busyModeDisplay, 'enum', false, Object.values(BUSY_MODE_DISPLAY));
    inputValidator.validateAndError('createViewport', branding.spinnerPositioning, 'enum', false, Object.values(SPINNER_POSITIONING));

    prop.sessionSettingsMode = prop.sessionSettingsMode !== undefined ? prop.sessionSettingsMode : SESSION_SETTINGS_MODE.FIRST;

    if (prop.sessionSettingsMode === SESSION_SETTINGS_MODE.MANUAL && !prop.sessionSettingsId)
        throw new ShapeDiverViewerValidationError('createViewport: Input could not be validated. sessionSettingsId has to point to a valid and created session when using SESSION_SETTINGS_MODE.MANUAL', prop.sessionSettingsId, 'string');

    const renderingEngine = await creationControlCenter.createRenderingEngineThreeJs(prop);

    viewports[renderingEngine.id] = new ViewportApi(renderingEngine);
    return viewports[renderingEngine.id];
};
